import React, { useMemo, useState } from "react";
import { Box, Checkbox, Divider, IconButton, InputAdornment, MenuItem, Paper, Select, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import TableSkeleton from "./TableSkeleton";
import EmptyState from "./EmptyState";
import { sortByLabel } from "../../utils/sortOptions";

function get(obj, path) {
  if (!path) return undefined;
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function toCSV(rows, columns) {
  const header = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = c.csvValue ? c.csvValue(r) : get(r, c.field);
          const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  return `${header}\n${body}`;
}

function downloadCSV(filename, csv) {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Keep filter menus attached to the Select. Default MUI menus are position:fixed
// on the viewport, so they drift away when the page (or a nested scroller) moves.
const SELECT_MENU_PROPS = {
  disablePortal: true,
  disableScrollLock: true,
  marginThreshold: 0,
  BackdropProps: {
    sx: { backgroundColor: "transparent" },
  },
  sx: {
    position: "absolute !important",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    overflow: "visible",
  },
  PaperProps: {
    sx: {
      position: "absolute !important",
      top: "calc(100% + 4px) !important",
      left: "0 !important",
      right: "auto !important",
      transform: "none !important",
      maxHeight: 320,
      width: "max-content !important",
      minWidth: "100% !important",
      maxWidth: 420,
    },
  },
  MenuListProps: {
    sx: {
      py: 0.5,
      minWidth: "100%",
      "& .MuiMenuItem-root": {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        px: 1.5,
        minHeight: 36,
      },
    },
  },
};

export default function DataTable({
  title,
  rows = [],
  columns = [],
  loading = false,
  searchable = true,
  searchPlaceholder = "Search...",
  filters = [],
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  onRowClick,
  selectable = false,
  selected = [],
  onSelectionChange,
  actions,
  emptyTitle = "No data",
  emptyDescription = "Nothing to show yet",
  emptyCTA,
  exportFilename = "export.csv",
  defaultSort,
  dense = false,
  rowKey = (r) => r._id || r.id,
  initialSearch = "",
}) {
  const theme = useTheme();
  // Render EITHER the desktop table OR the mobile card list — never both — so the
  // hidden variant can't create duplicate matches. The E2E suite runs at the
  // Playwright default 1280px (desktop), where this is false, so only the table
  // ever exists in the DOM under test. noSsr avoids a first-paint flash in CRA.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const [search, setSearch] = useState(initialSearch);
  const [filterValues, setFilterValues] = useState(() => Object.fromEntries(filters.map((f) => [f.key, "all"])));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState(defaultSort?.field || null);
  const [sortDir, setSortDir] = useState(defaultSort?.direction || "asc");

  const filtered = useMemo(() => {
    let out = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => {
          if (c.searchable === false) return false;
          const v = c.searchValue ? c.searchValue(r) : get(r, c.field);
          return v != null && String(v).toLowerCase().includes(q);
        })
      );
    }
    for (const f of filters) {
      const v = filterValues[f.key];
      if (v && v !== "all") {
        out = out.filter((r) => (f.predicate ? f.predicate(r, v) : get(r, f.field) === v));
      }
    }
    if (sortBy) {
      const col = columns.find((c) => c.field === sortBy);
      out = [...out].sort((a, b) => {
        const av = col?.sortValue ? col.sortValue(a) : get(a, sortBy);
        const bv = col?.sortValue ? col.sortValue(b) : get(b, sortBy);
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [rows, columns, search, filterValues, filters, sortBy, sortDir]);

  const paged = useMemo(() => filtered.slice(page * pageSize, page * pageSize + pageSize), [filtered, page, pageSize]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  const handleExport = () => {
    const csv = toCSV(filtered, columns.filter((c) => c.exportable !== false));
    downloadCSV(exportFilename, csv);
  };

  const allSelected = paged.length > 0 && paged.every((r) => selected.includes(rowKey(r)));
  const someSelected = paged.some((r) => selected.includes(rowKey(r))) && !allSelected;
  const toggleAll = () => {
    if (allSelected) onSelectionChange?.(selected.filter((id) => !paged.some((r) => rowKey(r) === id)));
    else onSelectionChange?.([...new Set([...selected, ...paged.map(rowKey)])]);
  };
  const toggleOne = (id) => {
    onSelectionChange?.(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "visible", scrollMarginBottom: "80px" }}>
      <Toolbar sx={{ gap: 1, flexWrap: "wrap", minHeight: { xs: "auto", sm: 56 }, py: { xs: 1.5, sm: 0 }, px: 2, borderBottom: "1px solid", borderColor: "divider", position: "relative", zIndex: 2, overflow: "visible" }}>
        {title && <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 2 }}>{title}</Typography>}
        {searchable && (
          <TextField
            size="small" placeholder={searchPlaceholder} value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: { xs: "100%", sm: 240 }, flexGrow: { xs: 1, sm: 0 } }}
          />
        )}
        {filters.map((f) => (
          <Stack key={f.key} direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <FilterListIcon fontSize="small" color="action" />
            {/* Accessible name for the combobox — screen readers were seeing
                an unlabeled "select" before (axe-core aria-input-field-name). */}
            <Box sx={{ position: "relative", zIndex: 3, overflow: "visible", minWidth: 200 }}>
              <Select
                size="small"
                autoWidth
                fullWidth
                displayEmpty
                value={filterValues[f.key]}
                inputProps={{ "aria-label": `Filter by ${f.label}` }}
                MenuProps={SELECT_MENU_PROPS}
                onChange={(e) => { setFilterValues({ ...filterValues, [f.key]: e.target.value }); setPage(0); }}
                sx={{
                  minWidth: 200,
                  overflow: "visible",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    pr: 4,
                  },
                }}
              >
                <MenuItem value="all">All {f.label}</MenuItem>
                {(f.sort === false ? f.options : sortByLabel(f.options)).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </Box>
          </Stack>
        ))}
        <Box sx={{ flex: 1 }} />
        {actions}
        <Tooltip title="Export CSV">
          <IconButton size="small" onClick={handleExport}><DownloadIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Toolbar>

      {isMobile ? (
        /* Mobile: stacked label/value cards instead of a horizontally-scrolling
           table. Columns can opt out of the card with `hideOnMobile`, or act as
           the card's heading with `primary: true`. */
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading ? (
            [0, 1, 2].map((i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Skeleton width="55%" height={22} />
                <Skeleton width="80%" />
                <Skeleton width="40%" />
              </Paper>
            ))
          ) : paged.length === 0 ? (
            <Box sx={{ py: 5 }}>
              <EmptyState title={emptyTitle} description={emptyDescription} cta={emptyCTA} />
            </Box>
          ) : (
            paged.map((r) => {
              const id = rowKey(r);
              const isSelected = selected.includes(id);
              const primaryCol = columns.find((c) => c.primary) || columns[0];
              const detailCols = columns.filter((c) => c !== primaryCol && !c.hideOnMobile);
              return (
                <Paper
                  key={id}
                  variant="outlined"
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  sx={{
                    p: 2, borderRadius: 2,
                    cursor: onRowClick ? "pointer" : "default",
                    borderColor: isSelected ? "primary.main" : "divider",
                    "&:active": onRowClick ? { bgcolor: "action.hover" } : undefined,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    {selectable && (
                      <Checkbox
                        edge="start" sx={{ mt: -0.5, ml: -1 }} checked={isSelected}
                        onClick={(e) => e.stopPropagation()} onChange={() => toggleOne(id)}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontWeight: 700, fontSize: "0.95rem", mb: detailCols.length ? 1 : 0 }}>
                        {primaryCol.render ? primaryCol.render(r) : get(r, primaryCol.field)}
                      </Box>
                      {detailCols.map((c, i) => {
                        const value = c.render ? c.render(r) : get(r, c.field);
                        if (value == null || value === "") return null;
                        return (
                          <React.Fragment key={c.field || c.label || i}>
                            {i > 0 && <Divider sx={{ my: 1 }} />}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} sx={{ py: 0.25 }}>
                              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}>
                                {c.label}
                              </Typography>
                              <Box sx={{ minWidth: 0, textAlign: "right", fontSize: "0.85rem" }}>{value}</Box>
                            </Stack>
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Stack>
                </Paper>
              );
            })
          )}
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size={dense ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                  </TableCell>
                )}
                {columns.map((c) => (
                  <TableCell key={c.field || c.label} align={c.align || "left"} sx={{ fontWeight: 600, whiteSpace: "nowrap", width: c.width }}>
                    {c.sortable !== false && c.field ? (
                      <TableSortLabel active={sortBy === c.field} direction={sortBy === c.field ? sortDir : "asc"} onClick={() => handleSort(c.field)}>
                        {c.label}
                      </TableSortLabel>
                    ) : c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton rows={5} cols={columns.length + (selectable ? 1 : 0)} />
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)} sx={{ py: 6 }}>
                    <EmptyState title={emptyTitle} description={emptyDescription} cta={emptyCTA} />
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((r) => {
                  const id = rowKey(r);
                  const isSelected = selected.includes(id);
                  return (
                    <TableRow
                      key={id}
                      hover={!!onRowClick}
                      onClick={onRowClick ? () => onRowClick(r) : undefined}
                      selected={isSelected}
                      sx={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      {selectable && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={isSelected} onChange={() => toggleOne(id)} />
                        </TableCell>
                      )}
                      {columns.map((c) => (
                        <TableCell key={c.field || c.label} align={c.align || "left"} sx={{ whiteSpace: c.wrap ? "normal" : "nowrap" }}>
                          {c.render ? c.render(r) : get(r, c.field)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
        rowsPerPageOptions={pageSizeOptions}
        // The default pagination bar (label + selector + range + arrows) overflows
        // a phone width; drop the "Rows per page" label on xs and tighten the
        // toolbar so it fits without a horizontal scrollbar.
        labelRowsPerPage={isMobile ? "" : "Rows per page:"}
        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
        sx={{
          position: "relative",
          zIndex: 2,
          "& .MuiTablePagination-toolbar": { pl: { xs: 1, sm: 2 }, flexWrap: "wrap" },
          "& .MuiTablePagination-actions": { ml: { xs: 0.5, sm: 2 } },
          "& .MuiTablePagination-select": { position: "relative" },
        }}
      />
    </Paper>
  );
}
